// Maps the "Exported" three-state toggle on the Invoice search form
// (-1 = Only exported, 0 = Only not exported, null = All) onto a filter
// over `Item.exported`. Returns null when no item-level filter should
// be applied.
//
// `Item.exported === 1` is the historical "exported" magic value. The
// filter is plumbed through the search form's `_item.exported` path,
// which the server resolves as an INNER-JOIN include — so a search for
// "Only exported" returns invoices that have at least one exported line
// item, matching the `invoice.isExported = items.some(...)` semantics
// already used on the detail page header.
const exportedItemFilter = exported => {
  if (exported === -1) return { value: 1, operator: "eq" };
  if (exported === 0) return { value: 1, operator: "ne" };
  return null;
};

// Pure transform that merges the Exported toggle into a server-shape
// search input. Called from the submit path alongside the other
// reference-sanitising step rather than via a Formik `useEffect`, to
// avoid mirroring `exported` into `_item.exported` on every render.
//
// Returns a new input; never mutates its arguments. When the toggle is
// indeterminate, any previous `_item.exported` filter is removed so a
// cleared toggle truly clears the filter.
const withExportedItemFilter = (input, exported) => {
  const filter = exportedItemFilter(exported);
  // strip any previous `_item.exported` filter so the spread below
  // doesn't leak a stale value when the toggle is cleared.
  const { exported: _previous, ...itemRest } = input?._item || {};

  if (filter === null) {
    const hasOtherItemFilters = Object.keys(itemRest).length > 0;
    return hasOtherItemFilters
      ? { ...input, _item: itemRest }
      : { ...input, _item: undefined };
  }

  return {
    ...input,
    _item: { ...itemRest, exported: filter },
  };
};

export { exportedItemFilter, withExportedItemFilter };
